import {
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Display,
  DisplayPosition,
  FlexDirection,
  JustifyContent,
  Overflow,
} from '@gousto-internal/citrus-react'
import type { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect } from 'react'

export type CarouselProps = {
  carouselAreaLabel: string
  children: React.ReactNode
  options?: EmblaOptionsType
}

export const Carousel = ({ carouselAreaLabel, children, options }: CarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options)

  const onSettle = useCallback((emblaApi) => {
    emblaApi.selectedScrollSnap()
    emblaApi.previousScrollSnap()
  }, [])

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext()
    }
  }, [emblaApi])

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('settle', onSettle)
    }
  }, [emblaApi, onSettle])

  return (
    <Box
      className="embla"
      position={DisplayPosition.Relative}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
    >
      <Box className="embla__viewport" ref={emblaRef}>
        <Box
          aria-label={carouselAreaLabel}
          aria-live="polite"
          aria-atomic="true"
          aria-roledescription="carousel"
          className="embla__container"
          display={Display.Flex}
          flexDirection={[FlexDirection.Column, FlexDirection.Row]}
          role="region"
          tabIndex={0}
        >
          {React.Children.map(children, (child, index) => {
            return (
              <Box
                paddingH={4}
                paddingV={4}
                aria-label={`${index + 1} of ${React.Children.count(children)}`}
                aria-roledescription={`Slide ${index + 1} of ${React.Children.count(children)}`}
                className="embla__slide"
                data-testid="carousel-slide"
                display={Display.Flex}
                justifyContent={JustifyContent.Center}
                flexGrow={0}
                flexShrink={0}
                key={index}
                role="group"
                tabIndex={-1}
              >
                {child}
              </Box>
            )
          })}
        </Box>

        <Box
          aria-label="Next slide control"
          display={[Display.None, Display.Flex, null, Display.None]}
          position={DisplayPosition.Absolute}
          role="group"
          top="45%"
          right="16px"
        >
          <Button
            aria-label="Next slide"
            className="embla__next"
            colorVariant={ButtonColorVariant.Secondary}
            icon="arrow_right"
            onClick={scrollNext}
            style={{ borderRadius: '50%' }}
            variant={ButtonVariant.Icon}
          />
        </Box>
      </Box>
    </Box>
  )
}
